<template>
  <b-modal
    id="user-detail-modal"
    ref="modal"
    size="xl"
    centered
    title="User Details"
    header-bg-variant="primary"
    header-text-variant="white"
    hide-footer
    @hidden="onModalHidden"
  >
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-4">
      <b-spinner variant="primary" label="Loading user details..."></b-spinner>
      <p class="mt-2 text-muted">Loading user information...</p>
    </div>

    <!-- User Details Content -->
    <div v-else-if="user">
      <!-- User Header -->
      <b-row class="mb-4">
        <b-col md="8">
          <div class="d-flex align-items-center">
            <div class="user-avatar mr-3">
              <i class="fas fa-user-circle fa-3x text-primary"></i>
            </div>
            <div>
              <h4 class="mb-1">
                {{ user.firstNameTh }} {{ user.lastNameTh }}
                <b-badge 
                  :variant="user.statusId === 1 ? 'success' : 'secondary'"
                  class="ml-2"
                >
                  {{ user.statusId === 1 ? 'Active' : 'Inactive' }}
                </b-badge>
              </h4>
              <p class="text-muted mb-0">
                {{ user.firstNameEn }} {{ user.lastNameEn }}
              </p>
              <small class="text-muted">
                User ID: {{ user.uid }} | 
                Type: {{ user.userTypeId === 1 ? 'Doctor' : 'Staff' }} |
                Registered: {{ formatDate(user.creationDt) }}
              </small>
            </div>
          </div>
        </b-col>
        <b-col md="4" class="text-right">
          <b-button-group>
            <b-button
              :variant="user.statusId === 1 ? 'warning' : 'success'"
              @click="toggleUserStatus"
              :disabled="statusUpdateLoading"
            >
              <i :class="user.statusId === 1 ? 'fas fa-user-slash' : 'fas fa-user-check'" class="mr-1"></i>
              {{ user.statusId === 1 ? 'Deactivate' : 'Activate' }}
            </b-button>
            <b-button variant="info" @click="exportUserData">
              <i class="fas fa-download mr-1"></i>
              Export
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>

      <!-- Tabs -->
      <b-tabs content-class="mt-3" justified>
        <!-- Basic Information Tab -->
        <b-tab title="Basic Info" active>
          <b-row>
            <b-col md="6">
              <b-card title="Personal Information" class="mb-3">
                <dl class="row">
                  <dt class="col-sm-4">Email:</dt>
                  <dd class="col-sm-8">{{ user.email }}</dd>
                  
                  <dt class="col-sm-4">Mobile:</dt>
                  <dd class="col-sm-8">{{ user.mobile || '-' }}</dd>
                  
                  <dt class="col-sm-4">User Type:</dt>
                  <dd class="col-sm-8">
                    <b-badge :variant="user.userTypeId === 1 ? 'info' : 'secondary'">
                      {{ user.userTypeId === 1 ? 'Doctor' : 'Staff' }}
                    </b-badge>
                  </dd>
                  
                  <dt class="col-sm-4">Status:</dt>
                  <dd class="col-sm-8">
                    <b-badge :variant="user.statusId === 1 ? 'success' : 'secondary'">
                      {{ user.statusId === 1 ? 'Active' : 'Inactive' }}
                    </b-badge>
                  </dd>
                </dl>
              </b-card>
            </b-col>
            
            <b-col md="6">
              <b-card title="Medical License" class="mb-3">
                <dl class="row">
                  <dt class="col-sm-4">License No:</dt>
                  <dd class="col-sm-8">
                    {{ user.drLicenseNo || '-' }}
                    <i v-if="user.drLicenseNo" class="fas fa-check-circle text-success ml-1" title="Has License"></i>
                  </dd>
                  
                  <dt class="col-sm-4">Specialties:</dt>
                  <dd class="col-sm-8">
                    <template v-if="user.drSpecialItems && user.drSpecialItems.length > 0">
                      <b-badge
                        v-for="specialty in user.drSpecialItems"
                        :key="specialty"
                        variant="outline-primary"
                        class="mr-1"
                      >
                        {{ getSpecialtyName(specialty) }}
                      </b-badge>
                    </template>
                    <span v-else class="text-muted">-</span>
                  </dd>
                  
                  <dt class="col-sm-4">Last Verified:</dt>
                  <dd class="col-sm-8">
                    {{ user.lastTdcLicenseVerifiedAt ? formatDate(user.lastTdcLicenseVerifiedAt) : '-' }}
                  </dd>
                </dl>
              </b-card>
            </b-col>
          </b-row>

          <!-- System Information -->
          <b-card title="System Information">
            <b-row>
              <b-col md="6">
                <dl class="row">
                  <dt class="col-sm-4">Created:</dt>
                  <dd class="col-sm-8">{{ formatDate(user.creationDt) }}</dd>
                  
                  <dt class="col-sm-4">Last Modified:</dt>
                  <dd class="col-sm-8">{{ formatDate(user.modifyDt) }}</dd>
                </dl>
              </b-col>
              <b-col md="6">
                <dl class="row">
                  <dt class="col-sm-4">Created By:</dt>
                  <dd class="col-sm-8">User ID: {{ user.creationUid || '-' }}</dd>
                  
                  <dt class="col-sm-4">Modified By:</dt>
                  <dd class="col-sm-8">User ID: {{ user.modifyUid || '-' }}</dd>
                </dl>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>

        <!-- Branch Memberships Tab -->
        <b-tab title="Branch Memberships">
          <div v-if="branchMemberships && branchMemberships.length > 0">
            <b-row>
              <b-col 
                v-for="membership in branchMemberships"
                :key="membership.membershipId"
                md="6"
                class="mb-3"
              >
                <b-card>
                  <template #header>
                    <h6 class="mb-0">
                      <i class="fas fa-clinic-medical mr-2"></i>
                      {{ membership.branch.branchNameTh }}
                    </h6>
                  </template>

                  <!-- Branch Information -->
                  <dl class="row mb-3">
                    <dt class="col-sm-5">Branch Code:</dt>
                    <dd class="col-sm-7">{{ membership.branch.branchCode }}</dd>
                    
                    <dt class="col-sm-5">English Name:</dt>
                    <dd class="col-sm-7">{{ membership.branch.branchNameEn || '-' }}</dd>
                    
                    <dt class="col-sm-5">Status:</dt>
                    <dd class="col-sm-7">
                      <b-badge :variant="membership.branch.status === 1 ? 'success' : 'secondary'">
                        {{ membership.branch.status === 1 ? 'Active' : 'Inactive' }}
                      </b-badge>
                    </dd>
                    
                    <dt class="col-sm-5">Address:</dt>
                    <dd class="col-sm-7">{{ membership.branch.address || '-' }}</dd>
                    
                    <dt class="col-sm-5">Phone:</dt>
                    <dd class="col-sm-7">{{ membership.branch.tel || '-' }}</dd>
                  </dl>

                  <!-- Clinic Information -->
                  <h6 class="border-top pt-3">
                    <i class="fas fa-building mr-2"></i>
                    Clinic Information
                  </h6>
                  <dl class="row mb-3">
                    <dt class="col-sm-5">Clinic Code:</dt>
                    <dd class="col-sm-7">{{ membership.clinic.clinicCode }}</dd>
                    
                    <dt class="col-sm-5">Clinic Name:</dt>
                    <dd class="col-sm-7">{{ membership.clinic.clinicNameTh }}</dd>
                    
                    <dt class="col-sm-5">Tax ID:</dt>
                    <dd class="col-sm-7">{{ membership.clinic.taxId || '-' }}</dd>
                    
                    <dt class="col-sm-5">Created:</dt>
                    <dd class="col-sm-7">{{ formatDate(membership.clinic.createdAt) }}</dd>
                  </dl>

                  <!-- Permissions -->
                  <h6 class="border-top pt-3">
                    <i class="fas fa-key mr-2"></i>
                    Permissions
                  </h6>
                  <div>
                    <b-badge
                      v-for="authId in membership.permissions.authIdItems"
                      :key="authId"
                      :variant="getPermissionVariant(authId)"
                      class="mr-1 mb-1"
                    >
                      {{ getPermissionName(authId) }}
                    </b-badge>
                  </div>

                  <!-- Membership Details -->
                  <small class="text-muted mt-3 d-block">
                    Member since: {{ formatDate(membership.membershipCreatedAt) }}
                  </small>
                </b-card>
              </b-col>
            </b-row>
          </div>
          <div v-else class="text-center py-4">
            <i class="fas fa-clinic-medical fa-3x text-muted mb-3"></i>
            <h5 class="text-muted">No Branch Memberships</h5>
            <p class="text-muted">This user is not a member of any branches.</p>
          </div>
        </b-tab>

        <!-- Activity Statistics Tab -->
        <b-tab title="Statistics" v-if="statistics">
          <b-row>
            <b-col md="6">
              <b-card text-variant="white" bg-variant="primary" class="mb-3">
                <b-card-text class="text-center">
                  <i class="fas fa-clinic-medical fa-2x mb-2"></i>
                  <h3 class="mb-0">{{ statistics.totalBranchMemberships }}</h3>
                  <small>Branch Memberships</small>
                </b-card-text>
              </b-card>
            </b-col>
            
            <b-col md="6">
              <b-card text-variant="white" bg-variant="success" class="mb-3">
                <b-card-text class="text-center">
                  <i class="fas fa-building fa-2x mb-2"></i>
                  <h3 class="mb-0">{{ statistics.totalClinics }}</h3>
                  <small>Clinics</small>
                </b-card-text>
              </b-card>
            </b-col>
          </b-row>
        </b-tab>
      </b-tabs>

      <!-- Modal Actions -->
      <div class="mt-4 text-right">
        <b-button variant="secondary" @click="closeModal">
          Close
        </b-button>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="text-center py-4">
      <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
      <h5 class="text-muted">Failed to load user details</h5>
      <p class="text-muted">Please try again or contact support if the problem persists.</p>
      <b-button variant="primary" @click="closeModal">
        Close
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';

export default {
  name: "UserDetailModal",
  data() {
    return {
      statusUpdateLoading: false,
      specialtyNames: {
        1: 'General Dentistry',
        2: 'Orthodontics',
        3: 'Oral Surgery',
        4: 'Periodontics',
        5: 'Endodontics',
        6: 'Prosthodontics',
        7: 'Pediatric Dentistry'
      },
      permissionNames: {
        1: 'Manager',
        2: 'Doctor',
        3: 'Counter',
        4: 'Assistant',
        5: 'Head Counter'
      }
    };
  },
  computed: {
    ...mapGetters('moduleSuperadmin', [
      'getSelectedUser',
      'isUserDetailsLoading'
    ]),
    user() {
      return this.getSelectedUser?.user;
    },
    branchMemberships() {
      return this.getSelectedUser?.branchMemberships;
    },
    statistics() {
      return this.getSelectedUser?.statistics;
    },
    isLoading() {
      return this.isUserDetailsLoading;
    }
  },
  methods: {
    ...mapActions('moduleSuperadmin', [
      'fetchUserDetails',
      'updateUserStatus',
      'clearSelectedUser'
    ]),
    
    async show(userId) {
      this.$bvModal.show('user-detail-modal');
      await this.fetchUserDetails(userId);
    },
    
    closeModal() {
      this.$bvModal.hide('user-detail-modal');
    },
    
    onModalHidden() {
      this.clearSelectedUser();
    },
    
    async toggleUserStatus() {
      if (!this.user) return;
      
      const newStatus = this.user.statusId === 1 ? 0 : 1;
      const action = newStatus === 1 ? 'activate' : 'deactivate';
      
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to ${action} this user?`,
        {
          title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: newStatus === 1 ? 'success' : 'warning',
          okTitle: `${action.charAt(0).toUpperCase() + action.slice(1)}`,
          cancelTitle: 'Cancel'
        }
      );
      
      if (confirmed) {
        this.statusUpdateLoading = true;
        try {
          await this.updateUserStatus({
            userId: this.user.uid,
            statusData: {
              statusId: newStatus,
              reason: `User ${action}d via superladmin interface`
            }
          });
          
          this.$bvToast.toast(`User ${action}d successfully`, {
            title: 'Status Updated',
            variant: 'success',
            autoHideDelay: 3000
          });
        } catch (error) {
          this.$bvToast.toast(`Failed to ${action} user. Please try again.`, {
            title: 'Error',
            variant: 'danger',
            autoHideDelay: 5000
          });
        } finally {
          this.statusUpdateLoading = false;
        }
      }
    },
    
    exportUserData() {
      // Create CSV data
      const csvData = this.generateUserCSV();
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `user_${this.user.uid}_details.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    
    generateUserCSV() {
      const user = this.user;
      const memberships = this.branchMemberships || [];
      
      let csv = 'User Information\n';
      csv += 'Field,Value\n';
      csv += `User ID,${user.uid}\n`;
      csv += `Name (Thai),${user.firstNameTh} ${user.lastNameTh}\n`;
      csv += `Name (English),${user.firstNameEn} ${user.lastNameEn}\n`;
      csv += `Email,${user.email}\n`;
      csv += `Mobile,${user.mobile || ''}\n`;
      csv += `User Type,${user.userTypeId === 1 ? 'Doctor' : 'Staff'}\n`;
      csv += `Status,${user.statusId === 1 ? 'Active' : 'Inactive'}\n`;
      csv += `License Number,${user.drLicenseNo || ''}\n`;
      csv += `Created,${this.formatDate(user.creationDt)}\n`;
      csv += `Last Modified,${this.formatDate(user.modifyDt)}\n`;
      
      csv += '\n\nBranch Memberships\n';
      csv += 'Branch Code,Branch Name,Clinic Code,Clinic Name,Permissions,Member Since\n';
      
      memberships.forEach(membership => {
        const permissions = membership.permissions.authIdItems.map(id => this.getPermissionName(id)).join(';');
        csv += `${membership.branch.branchCode},${membership.branch.branchNameTh},${membership.clinic.clinicCode},${membership.clinic.clinicNameTh},"${permissions}",${this.formatDate(membership.membershipCreatedAt)}\n`;
      });
      
      return csv;
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return moment(dateStr).format('YYYY-MM-DD HH:mm:ss');
    },
    
    getSpecialtyName(specialtyId) {
      return this.specialtyNames[specialtyId] || `Specialty ${specialtyId}`;
    },
    
    getPermissionName(authId) {
      return this.permissionNames[authId] || `Permission ${authId}`;
    },
    
    getPermissionVariant(authId) {
      const variants = {
        1: 'danger',    // Manager - red
        2: 'primary',   // Doctor - blue  
        3: 'success',   // Counter - green
        4: 'info',      // Assistant - cyan
        5: 'warning'    // Head Counter - yellow
      };
      return variants[authId] || 'secondary';
    }
  }
};
</script>

<style scoped>
.user-detail-modal .modal-header {
  border-bottom: 1px solid #dee2e6;
}

.user-detail-modal .card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
}

.user-detail-modal .dl-horizontal dt {
  font-weight: 600;
  color: #495057;
}

.user-detail-modal .user-avatar {
  flex-shrink: 0;
}

.user-detail-modal .badge {
  font-size: 0.75rem;
}

.user-detail-modal .card-text h3 {
  font-size: 2rem;
  font-weight: 700;
}

.user-detail-modal .card-text small {
  font-size: 0.875rem;
  opacity: 0.8;
}

.user-detail-modal .fa-2x {
  font-size: 2rem;
}

.user-detail-modal .fa-3x {
  font-size: 3rem;
}

/* Tab content spacing */
.user-detail-modal .tab-content {
  padding-top: 1rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .user-detail-modal .dl-horizontal dt {
    font-size: 0.875rem;
  }
  
  .user-detail-modal .dl-horizontal dd {
    font-size: 0.875rem;
  }
}
</style>